import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Money, Airplane, Info, CheckCircle, ClipboardText } from '@phosphor-icons/react';
import { Sar } from '@/components/SarIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_finance:            { ar: 'بانتظار المدير المالي', en: 'Awaiting Finance Manager', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  pending_employee_signature: { ar: 'بانتظار توقيعك',          en: 'Awaiting Your Signature',  cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  signed:                     { ar: 'مكتمل',                    en: 'Completed',                cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:                   { ar: 'مرفوض',                    en: 'Rejected',                 cls: 'bg-red-100 text-red-800 border-red-200' },
  cancelled_unsigned:         { ar: 'مُلغى (لم يُوقَّع)',          en: 'Cancelled (unsigned)',     cls: 'bg-red-100 text-red-800 border-red-200' },
};

export default function CashOnlyEncashmentSection({ onSubmitted, onOpenClearance }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [eligibility, setEligibility] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [days, setDays] = useState('');
  const [notes, setNotes] = useState('');
  const [requestTicket, setRequestTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [el, mine] = await Promise.all([
        axios.get(`${API}/clearance/cash-only/eligibility`, { withCredentials: true }),
        axios.get(`${API}/clearance/my`, {
          withCredentials: true,
          params: { _t: Date.now() },
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        }),
      ]);
      setEligibility(el.data);
      setRequests((mine.data || []).filter((c) => c.type === 'cash_only'));
    } catch (e) {
      // silent — page already shows errors elsewhere
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async () => {
    const d = parseFloat(days);
    if (!d || d <= 0) {
      toast.error(isAr ? 'أدخل عدد أيام صحيح' : 'Enter a valid number of days');
      return;
    }
    if (eligibility && d > (eligibility.max_cashable_days || 0)) {
      toast.error(
        isAr
          ? `الأيام المطلوبة (${d}) تتجاوز رصيدك (${eligibility.max_cashable_days})`
          : `Requested days (${d}) exceed your balance (${eligibility.max_cashable_days})`
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/clearance/cash-only/request`,
        { requested_days: d, notes, ticket_requested: !!requestTicket },
        { withCredentials: true }
      );
      toast.success(isAr ? 'تم إرسال الطلب للمدير المالي' : 'Request sent to Finance Manager');
      setShowDialog(false);
      setDays(''); setNotes(''); setRequestTicket(false);
      fetchAll();
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذر إرسال الطلب' : 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const maxDays = eligibility?.max_cashable_days || 0;
  const trueBalance = eligibility?.true_balance || 0;
  const policy = eligibility?.policy || {};
  const minBalanceRequired = policy.min_balance_required ?? 40;
  const maxRequestPerYear = policy.max_request_per_year ?? 30;
  const residualDays = policy.residual_days_required ?? 10;
  const eligibleMinBalance = policy.eligible_min_balance ?? false;
  const contractedBlocked = policy.contracted_worker_blocked === true;
  const dailyRate = eligibility?.historic_avg_rate || eligibility?.daily_full_wage || 0;
  // Air-ticket opt-in surfaces only when the employee's REAL balance exceeds
  // 30 days (independent of the per-request cap which is also 30).
  const ticketEligible = trueBalance > 30;
  const estimate = (parseFloat(days) || 0) * dailyRate;
  // Validity check used to disable the submit button AND to highlight the input
  // when the typed value exceeds the cashable balance.
  const parsedDays = parseFloat(days);
  const exceedsBalance = Number.isFinite(parsedDays) && parsedDays > maxDays + 0.01;
  const tooLow = Number.isFinite(parsedDays) && parsedDays <= 0;
  const canSubmit = !submitting && Number.isFinite(parsedDays) && !exceedsBalance && !tooLow && eligibleMinBalance;

  // Clamp on change — refuse any keystroke that would push days above maxDays.
  const handleDaysChange = (raw) => {
    if (raw === '' || raw === '-') { setDays(''); return; }
    const v = parseFloat(raw);
    if (Number.isNaN(v)) return; // ignore non-numeric input
    if (v < 0) { setDays('0'); return; }
    if (v > maxDays) {
      setDays(String(maxDays));
      return;
    }
    setDays(raw);
  };

  // Contracted workers are paid by their supplier — hide the entire
  // cash-out card to avoid confusion. They keep all other features.
  if (contractedBlocked) return null;

  return (
    <Card className="border border-[#C4BFF0] bg-gradient-to-br from-[#F2F0FA] to-white">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
          <Money size={22} weight="duotone" className="text-[#655dc6]" />
          {isAr ? 'صرف نقدي بدل إجازة (بدون سفر)' : 'Cash-Only Leave Encashment (No Travel)'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-[#655dc6]/80 leading-relaxed">
          {isAr
            ? 'إذا قررت عدم السفر وتريد صرف بدل إجازتك نقداً مع قيمة تذكرة الطيران، يمكنك تقديم طلب صرف نقدي. يذهب الطلب مباشرة للمدير المالي ولا يحتاج موافقات سلسلة الإجازات. عند الاعتماد والتوقيع يتم خصم عدد الأيام من رصيد إجازتك.'
            : 'If you decided not to travel and want a cash payout for your leave plus the air-ticket value, you may submit a cash-only request. It goes directly to the Finance Manager — no leave-approval chain. On approval & signature the days are deducted from your balance.'}
        </p>

        {/* Policy summary — surfaces the cash-out thresholds. */}
        <div
          className="rounded-lg bg-indigo-50/60 border border-indigo-200 p-3 text-[11px] text-indigo-900 leading-snug"
          data-testid="cash-only-policy"
        >
          <p className="font-semibold mb-1">
            {isAr ? '📋 شروط الصرف النقدي:' : '📋 Cash-out policy:'}
          </p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>
              {isAr
                ? <>الحد الأدنى للرصيد: <strong>{minBalanceRequired}</strong> يوماً.</>
                : <>Minimum balance required: <strong>{minBalanceRequired}</strong> days.</>}
            </li>
            <li>
              {isAr
                ? <>الحد الأقصى لكل طلب: <strong>{maxRequestPerYear}</strong> يوماً.</>
                : <>Maximum per request: <strong>{maxRequestPerYear}</strong> days.</>}
            </li>
            <li>
              {isAr
                ? <>تُخصم الأيام المصروفة من رصيد إجازاتك الكلي بعد التوقيع.</>
                : <>The cashed-out days are deducted from your total leave balance upon signature.</>}
            </li>
          </ul>
        </div>

        {/* Eligibility / status banner */}
        {loading ? (
          <div className="h-12 flex items-center justify-center text-[#655dc6]/60 text-xs">
            {isAr ? 'جاري التحميل…' : 'Loading…'}
          </div>
        ) : !eligibleMinBalance ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800">
            <Info size={16} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-0.5">
                {isAr ? 'لا تستوفي شروط الصرف النقدي حالياً' : 'You do not meet the cash-out requirements yet'}
              </div>
              <div className="text-[11px] text-rose-700/90">
                {isAr
                  ? <>رصيدك الحالي: <strong>{trueBalance}</strong> يوماً · الحد الأدنى المطلوب: <strong>{minBalanceRequired}</strong> يوماً.</>
                  : <>Your balance: <strong>{trueBalance}</strong> day(s) · Required minimum: <strong>{minBalanceRequired}</strong> day(s).</>}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-xs text-emerald-800 leading-snug">
              <div className="font-semibold">
                {isAr ? 'متاح لك تقديم طلب صرف نقدي' : 'You are eligible to submit a cash-out request'}
              </div>
              <div className="text-[11px] text-emerald-700/80 mt-0.5">
                {isAr
                  ? <>رصيدك: <strong>{trueBalance}</strong> يوماً · القابل للصرف: <strong>{maxDays}</strong> يوماً</>
                  : <>Your balance: <strong>{trueBalance}</strong>d · Cashable: <strong>{maxDays}</strong>d</>}
                {dailyRate > 0 && (
                  <> · {isAr ? 'الأجر اليومي:' : 'daily rate:'} <strong>{dailyRate.toLocaleString?.()}</strong> <Sar /></>
                )}
              </div>
            </div>
            <Button
              onClick={() => setShowDialog(true)}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
              size="sm"
              data-testid="open-cash-only-dialog"
              disabled={maxDays <= 0}
            >
              <Money size={16} className="me-2" />
              {isAr ? 'تقديم طلب صرف' : 'Submit Request'}
            </Button>
          </div>
        )}

        {/* My cash-only requests */}
        {requests.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-[11px] font-semibold text-[#655dc6]/70">
              {isAr ? 'طلباتي السابقة' : 'My previous requests'}
            </p>
            {requests.map((r) => {
              const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending_finance;
              const canSign = r.status === 'pending_employee_signature' && typeof onOpenClearance === 'function';
              return (
                <div
                  key={r.id}
                  onClick={canSign ? () => onOpenClearance(r.id) : undefined}
                  className={`flex items-center justify-between gap-2 p-2 rounded-md bg-white border text-xs ${
                    canSign
                      ? 'border-blue-300 bg-blue-50 hover:bg-blue-100 cursor-pointer ring-1 ring-blue-200'
                      : 'border-[#C4BFF0]'
                  }`}
                  data-testid={`cash-only-${r.id}`}
                >
                  <div className="flex items-center gap-3">
                    <ClipboardText size={14} className="text-[#655dc6]" />
                    <span className="font-medium text-[#655dc6]">
                      {r.requested_days} {isAr ? 'يوم' : 'd'}
                    </span>
                    {(r.final_ticket_amount > 0) && (
                      <span className="text-[#655dc6]/70 flex items-center gap-1" title={isAr ? 'قيمة تذكرة طيران معتمدة من المدير المالي' : 'Air-ticket value approved by Finance Manager'}>
                        <Airplane size={12} /> {r.final_ticket_amount.toLocaleString?.()} <Sar />
                      </span>
                    )}
                    <span className="text-[10px] text-[#655dc6]/50">
                      {(r.created_at || '').slice(0, 10)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {canSign && r.days_remaining_for_signature != null && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${
                          r.days_remaining_for_signature <= 2
                            ? 'bg-red-100 text-red-700 border border-red-300'
                            : 'bg-amber-100 text-amber-800 border border-amber-200'
                        }`}
                        title={isAr
                          ? 'يجب التوقيع خلال 10 أيام من اعتماد الإدارة وإلا تُلغى المخالصة تلقائياً'
                          : 'Sign within 10 days of approval — auto-cancelled otherwise'}
                      >
                        ⏳ {r.days_remaining_for_signature > 0
                          ? (isAr ? `متبقي ${r.days_remaining_for_signature} يوم` : `${r.days_remaining_for_signature}d left`)
                          : (isAr ? `متبقي ${r.hours_remaining_for_signature || 0} ساعة` : `${r.hours_remaining_for_signature || 0}h left`)}
                      </span>
                    )}
                    {canSign && (
                      <span className="text-[11px] font-semibold text-blue-700">
                        {isAr ? 'اضغط للتوقيع ←' : 'Click to sign →'}
                      </span>
                    )}
                    {r.status === 'signed' && r.final_net > 0 && (
                      <span className="text-[11px] text-emerald-700">
                        {isAr ? 'صافي' : 'Net'}: <strong>{r.final_net.toLocaleString?.()}</strong> <Sar />
                      </span>
                    )}
                    <Badge className={s.cls}>{isAr ? s.ar : s.en}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" dir={isAr ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {isAr ? 'صرف نقدي بدل إجازة' : 'Cash-Only Encashment'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {isAr
                ? `الرصيد القابل للصرف: ${maxDays} يوم · يذهب للمدير المالي مباشرة`
                : `Cashable balance: ${maxDays} days · Routed straight to Finance Manager`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-start block mb-1">
                {isAr ? 'عدد الأيام المطلوب صرفها' : 'Days to cash out'} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number" min="0.5" step="0.5" max={maxDays}
                value={days}
                onChange={(e) => handleDaysChange(e.target.value)}
                placeholder={maxDays.toString()}
                className={exceedsBalance ? 'border-red-500 focus-visible:ring-red-500' : ''}
                data-testid="input-cash-only-days"
              />
              {exceedsBalance ? (
                <p className="text-[11px] text-red-600 mt-1" data-testid="cash-days-error">
                  {isAr
                    ? `الحد الأقصى المسموح هو ${maxDays} يوم`
                    : `Maximum allowed is ${maxDays} day(s)`}
                </p>
              ) : (
                <p className="text-[10px] text-[#655dc6]/60 mt-1">
                  {isAr
                    ? `الحد الأقصى: ${maxDays} يوم`
                    : `Max: ${maxDays} day(s)`}
                </p>
              )}
            </div>

            {/* Air-ticket REQUEST opt-in — shown ONLY if employee balance > 30 days.
                Employee only signals intent; the Finance Manager decides the value. */}
            {ticketEligible && (
              <label
                htmlFor="ticket-request-checkbox"
                className="flex items-start gap-2 p-2 rounded-md bg-blue-50 border border-blue-200 text-[11px] text-blue-800 leading-snug cursor-pointer hover:bg-blue-100 transition-colors"
                data-testid="ticket-request-label"
              >
                <input
                  id="ticket-request-checkbox"
                  type="checkbox"
                  checked={requestTicket}
                  onChange={(e) => setRequestTicket(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-600 cursor-pointer shrink-0"
                  data-testid="input-ticket-requested"
                />
                <Airplane size={14} weight="duotone" className="text-blue-600 shrink-0 mt-0.5" />
                <span>
                  {isAr
                    ? <><strong>أرغب بقيمة تذكرة الطيران</strong> ضمن طلب الصرف النقدي. <span className="text-blue-700/70">القيمة النهائية يحدّدها المدير المالي وتظهر في نموذج المخالصة قبل التوقيع.</span></>
                    : <><strong>I would like an air-ticket value</strong> with this cash-out. <span className="text-blue-700/70">The Finance Manager sets the final amount in the clearance form before signing.</span></>}
                </span>
              </label>
            )}

            <div>
              <Label className="text-xs text-start block mb-1">{isAr ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={isAr ? 'اختياري' : 'Optional'}
                data-testid="input-cash-only-notes"
              />
            </div>
            {(parseFloat(days) > 0) && (
              <div className="p-2 rounded-md bg-[#E8E6F5] border border-[#C4BFF0] text-xs text-[#655dc6] flex items-center justify-between">
                <span>{isAr ? 'الإجمالي التقديري' : 'Estimated total'}</span>
                <strong>{estimate.toLocaleString?.()} <Sar /></strong>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={submitting} data-testid="cancel-cash-only">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-cash-only"
            >
              <CheckCircle size={16} className="me-2" />
              {submitting ? (isAr ? 'جاري الإرسال…' : 'Submitting…') : (isAr ? 'إرسال الطلب' : 'Submit Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
